import { Form } from '@ant-design/compatible';

import { Row, Col, Input, PageHeader, Select, Checkbox, Tooltip } from 'antd';
import { parseISO, format } from 'date-fns';
import { AdminListing, ListingStatus, LISTING_STATUSES, LEASE_TYPES } from '@root/types';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Button from '@components/shared/V2Button';

type Props = {
  listing: AdminListing;
  form: any; // not worth getting the type from antd 3 forms
};

const ListingDetailsSection = ({ listing, form }: Props) => {
  const { t } = useTranslation('admin');
  const { getFieldDecorator } = form;
  const { Option } = Select;
  const liveListingStatuses: ListingStatus[] = ['on_market', 'off_market'];
  const flags = useFlags();
  const canSetCeilingHeightUnits = flags['setting-ceiling-height-units'];

  const viewListingButtonText = (status: ListingStatus) => {
    if (liveListingStatuses.includes(status)) {
      return t('listing.viewListingButton.live');
    }

    return t('listing.viewListingButton.preview');
  };

  return (
    <>
      <PageHeader title={t('listing.listingDataTitle')} />
      <Form.Item label={t('listing.vtsSpaceId')}>
        {getFieldDecorator('vtsSpaceId', { initialValue: listing.space.vtsId })(<Input disabled />)}
      </Form.Item>
      <Form.Item label={t('listing.vtsMarketplaceListingId')}>
        {getFieldDecorator('vtsMarketplaceListingId', { initialValue: listing.id })(
          <Input disabled />,
        )}
      </Form.Item>
      <Form.Item label={t('listing.vtsFloorName')}>
        {getFieldDecorator('vtsFloorName', { initialValue: listing.space.floorName })(
          <Input disabled />,
        )}
      </Form.Item>
      <Form.Item label={t('listing.displayFloorName')} required>
        {getFieldDecorator('floor', {
          initialValue: listing.floor,
          rules: [
            {
              required: true,
              message: t('listing.floorNameValidation'),
              suppressWarnings: true,
            },
          ],
        })(<Input />)}
      </Form.Item>
      <Form.Item label={t('listing.vtsSuiteName')}>
        {getFieldDecorator('vtsSuiteName', { initialValue: listing.space.suite })(
          <Input disabled />,
        )}
      </Form.Item>
      <Form.Item label={t('listing.displaySuiteName')}>
        {getFieldDecorator('suite', { initialValue: listing.suite })(<Input />)}
      </Form.Item>
      <Form.Item label={t('listing.availabilityType')}>
        {getFieldDecorator('availabilityType', { initialValue: listing.space.availabilityType })(
          <Input disabled />,
        )}
      </Form.Item>
      <Form.Item label={t('listing.availabilityDate')}>
        {getFieldDecorator('availabilityDate', {
          initialValue: listing.space.availabilityDate,
        })(<Input disabled />)}
      </Form.Item>
      <Form.Item label={t('listing.size')}>
        {getFieldDecorator('spaceArea', {
          initialValue: get(listing.space.remeasuredArea, 'magnitude'),
        })(<Input suffix={t(`units:${get(listing.space.remeasuredArea, 'unit', '')}`)} disabled />)}
      </Form.Item>
      <Form.Item label={t('listing.maximumContiguousArea')}>
        {getFieldDecorator('maximumContiguousArea', {
          initialValue: get(listing.space.maximumContiguousArea, 'magnitude'),
        })(
          <Input
            suffix={t(`units:${get(listing.space.maximumContiguousArea, 'unit', '')}`)}
            disabled
          />,
        )}
      </Form.Item>
      <Form.Item label={t('listing.floorComposition')}>
        {getFieldDecorator('floorComposition', {
          initialValue: listing.space.floorComposition,
        })(<Input disabled />)}
      </Form.Item>
      <Form.Item label={t('listing.condition')}>
        {getFieldDecorator('condition', {
          initialValue: listing.space.condition,
        })(<Input disabled />)}
      </Form.Item>
      <Form.Item label={t('listing.listingStatus')}>
        {getFieldDecorator('status', { initialValue: listing.status })(
          <Select>
            {LISTING_STATUSES.map(status => (
              <Option key={status} value={status}>
                {t(`listing.statuses.${status}`)}
              </Option>
            ))}
          </Select>,
        )}
        {listing.status !== 'retired' ? (
          <a href={listing.url as string} target="_blank" rel="noopener noreferrer">
            <Button type="secondary" data-testid="view-listing-button">
              {viewListingButtonText(listing.status)}
            </Button>
          </a>
        ) : null}
      </Form.Item>
      <Form.Item label={t('listing.requestedDate')}>
        {getFieldDecorator('requestedAt', {
          initialValue: listing.requestedAt ? format(parseISO(listing.requestedAt), 'PP - p') : '',
        })(<Input disabled />)}
      </Form.Item>
      <Form.Item label={t('listing.onMarketDate')}>
        {getFieldDecorator('onMarketAt', {
          initialValue: listing.onMarketAt ? format(parseISO(listing.onMarketAt), 'M/d/yyyy') : '',
        })(<Input disabled />)}
      </Form.Item>
      <Form.Item label={t('listing.offMarketDate')}>
        {getFieldDecorator('offMarketAt', {
          initialValue: listing.offMarketAt
            ? format(parseISO(listing.offMarketAt), 'M/d/yyyy')
            : '',
        })(<Input disabled />)}
      </Form.Item>
      <Tooltip trigger="hover" placement="topLeft" title={t('listing.notMarketableTooltip')}>
        <Form.Item label={t('listing.notMarketable')}>
          {getFieldDecorator('notMarketable', {
            initialValue: listing.notMarketable,
            valuePropName: 'checked',
          })(<Checkbox />)}
        </Form.Item>
      </Tooltip>
      <Form.Item label={t('listing.exclusive')}>
        {getFieldDecorator('exclusive', {
          initialValue: listing.exclusive,
          valuePropName: 'checked',
        })(
          <Checkbox
            disabled={
              !!listing.onMarketAt &&
              new Date(new Date(listing.onMarketAt).getTime() + 30 * 24 * 60 * 60 * 1000).setHours(
                0,
                0,
                0,
                0,
              ) <= new Date().setHours(0, 0, 0, 0)
            }
          />,
        )}
      </Form.Item>
      <Form.Item label={t('listing.exclusiveUntilDate')}>
        {getFieldDecorator('exclusiveUntil', {
          initialValue: listing.exclusiveUntil
            ? format(parseISO(listing.exclusiveUntil), 'M/d/yyyy')
            : '',
        })(<Input disabled />)}
      </Form.Item>

      <Row gutter={[8, 0]}>
        <Col span={12}>
          <Form.Item
            label={t('listing.slabToSlabHeight')}
            labelCol={{ sm: { span: 10 } }}
            wrapperCol={{ flex: 'flex' }}
          >
            {getFieldDecorator('slabToSlabHeightMeasure.magnitude', {
              initialValue: listing.slabToSlabHeightMeasure
                ? listing.slabToSlabHeightMeasure.magnitude
                : '',
            })(<Input type="number" />)}
          </Form.Item>
        </Col>
        <Col span={3}>
          {canSetCeilingHeightUnits ? (
            <Form.Item wrapperCol={{ flex: 'flex' }}>
              {getFieldDecorator('slabToSlabHeightMeasure.units.length', {
                initialValue: listing.slabToSlabHeightMeasure
                  ? listing.slabToSlabHeightMeasure.units.length
                  : listing.buildingCeilingHeightUnits,
              })(<Input disabled />)}
            </Form.Item>
          ) : (
            <Form.Item wrapperCol={{ flex: 'flex' }}>
              {getFieldDecorator('slabToSlabHeightMeasure.units.length', {
                initialValue: listing.slabToSlabHeightMeasure
                  ? listing.slabToSlabHeightMeasure.units.length
                  : '',
              })(
                <Select
                  options={[
                    {
                      label: t('units:inches'),
                      value: 'Inches',
                    },
                    {
                      label: t('units:centimeters'),
                      value: 'Centimeters',
                    },
                  ]}
                />,
              )}
            </Form.Item>
          )}
        </Col>
      </Row>

      <Row gutter={[8, 0]}>
        <Col span={12}>
          <Form.Item
            label={t('listing.finishedCeilingHeight')}
            labelCol={{ sm: { span: 10 } }}
            wrapperCol={{ flex: 'flex' }}
          >
            {getFieldDecorator('finishedCeilingHeightMeasure.magnitude', {
              initialValue: listing.finishedCeilingHeightMeasure
                ? listing.finishedCeilingHeightMeasure.magnitude
                : '',
            })(<Input type="number" />)}
          </Form.Item>
        </Col>
        <Col span={3}>
          {canSetCeilingHeightUnits ? (
            <Form.Item wrapperCol={{ flex: 'flex' }}>
              {getFieldDecorator('finishedCeilingHeightMeasure.units.length', {
                initialValue: listing.finishedCeilingHeightMeasure
                  ? listing.finishedCeilingHeightMeasure.units.length
                  : listing.buildingCeilingHeightUnits,
              })(<Input disabled />)}
            </Form.Item>
          ) : (
            <Form.Item wrapperCol={{ flex: 'flex' }}>
              {getFieldDecorator('finishedCeilingHeightMeasure.units.length', {
                initialValue: listing.finishedCeilingHeightMeasure
                  ? listing.finishedCeilingHeightMeasure.units.length
                  : '',
              })(
                <Select
                  options={[
                    {
                      label: t('units:inches'),
                      value: 'Inches',
                    },
                    {
                      label: t('units:centimeters'),
                      value: 'Centimeters',
                    },
                  ]}
                />,
              )}
            </Form.Item>
          )}
        </Col>
      </Row>
      <Form.Item label={t('listing.maximumCapacityPersons')}>
        {getFieldDecorator('maximumCapacityPersons', {
          initialValue: listing.maximumCapacityPersons,
        })(<Input />)}
      </Form.Item>
      <Form.Item label={t('listing.leaseType')}>
        {getFieldDecorator('leaseType', {
          initialValue: listing.leaseType,
        })(
          <Select>
            {LEASE_TYPES.map(leaseType => (
              <Option key={leaseType} value={leaseType}>
                {t(`listing.leaseTypes.${leaseType}`)}
              </Option>
            ))}
          </Select>,
        )}
      </Form.Item>
    </>
  );
};

export default ListingDetailsSection;
